import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserManagementUser } from '@app/shared/models/user-management-user';
import { TeamService } from '@app/shared/services/team/team.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { UserManagementService } from '@app/shared/services/user-management/user-management.service';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-user-management-invite',
  templateUrl: './user-management-invite.component.html',
  styleUrls: ['./user-management-invite.component.scss'],
})
export class UserManagementInviteComponent implements OnInit {
  userForm: UntypedFormGroup;
  user: UserManagementUser;
  saving: boolean;
  deleting: boolean;
  mode: 'new' | 'edit' = 'new';

  team$ = this.teamService.getTeamMembers(null, null, null, null, false);
  team: any[];
  filteredTeam: Observable<any[]> = of([]);

  constructor(
    private fb: UntypedFormBuilder,
    private userMgtService: UserManagementService,
    private router: Router,
    private route: ActivatedRoute,
    private teamService: TeamService,
    private titleService: TitleService
  ) { }

  ngOnInit() {
    this.user = null;
    this.determineMode();
    this.setupForm();

    this.team$.subscribe((res: any) => {
      this.team = res.data;

      this.filteredTeam = this.userForm.get('first_name').valueChanges.pipe(
        startWith(''),
        map((value) => this._filter(value))
      );
    });
  }

  private _filter(value: string): string[] {
    if (typeof value !== 'string') {
      return;
    }

    const filterValue = value.toLowerCase();

    return this.team.filter((option) => option.first_name.toLowerCase().includes(filterValue));
  }

  autoCompleteSelected($event: MatAutocompleteSelectedEvent) {
    this.userForm.get('first_name').setValue($event.option.value.first_name);
    this.userForm.get('last_name').setValue($event.option.value.last_name);
    this.userForm.get('email').setValue($event.option.value.email);
  }

  determineMode() {
    this.route.params.subscribe((params) => {
      this.mode = params.userId ? 'edit' : 'new';

      if (this.mode === 'new') {
        this.initializeNewUser();
      } else {
        this.fetchUserFromApi(params.userId);
      }
    });
  }

  fetchUserFromApi(userId: string) {
    this.userMgtService.getSingleUserFromApi(userId).subscribe((res) => {
      this.user = res;
      this.user.permissions.features = this.allFeatures;

      this.userForm.get('first_name').setValue(this.user.first_name);
      this.userForm.get('last_name').setValue(this.user.last_name);
      this.userForm.get('email').setValue(this.user.email);
      this.userForm.get('permission_type').setValue(this.user.permission_type);

      this.titleService.manuallySet(`${this.user.first_name} ${this.user.last_name}`);
    });
  }

  initializeNewUser() {
    this.user = {
      first_name: '',
      last_name: '',
      email: '',
      permission_type: 'all',
      permissions: {
        is_admin: false,
        all_features: false,
        all_properties: false,
        all_financials: false,
        is_team_owner: false,
        features: this.allFeatures,
      },
      all_properties: true,
      property_ids: [],
    };
    this.featuresToObject(this.user, this.user.permissions.features);
  }

  private get allFeatures() {
    return Object.values(Features);
  }

  setupForm() {
    this.userForm = this.fb.group({
      first_name: [, [Validators.required]],
      last_name: [, [Validators.required]],
      email: new UntypedFormControl({ value: '', disabled: this.mode === 'edit' }, Validators.required),
      permission_type: ['all'],
    });
  }

  deleteUserClicked() {
    this.deleting = true;

    this.userMgtService.deleteUser(this.user.id).subscribe((res) => {
      this.router.navigate(['/settings/user-management']);
    });
  }

  inviteClicked() {
    this.user.first_name = this.userForm.get('first_name').value;
    this.user.last_name = this.userForm.get('last_name').value;
    this.user.email = this.userForm.get('email').value;
    this.user.permission_type = this.userForm.get('permission_type').value;

    this.saving = true;

    if (this.mode === 'new') {
      this.userMgtService.createUser(this.user).subscribe((res) => {
        if (res) {
          this.saving = false;
          this.router.navigate(['/settings/user-management']);
        } else {
          this.saving = false;
        }
      });
    } else {
      this.userMgtService.updateUser(this.user).subscribe((res) => {
        this.saving = false;
        if (res) {
          this.ngOnInit();
        } else {
          // 422 error will be handled by the interceptor
        }
      });
    }
  }

  propertiesChanged($event) {
    this.user.all_properties = $event.all_properties;
    this.user.property_ids = $event.properties;
  }

  permissionsChanged(permissions) {
    this.user.permissions = permissions;
  }

  featuresToObject(user, features: Features[]) {
    for (let i = 0; i < features.length; i++) {
      user.permissions[features[i]] = false;
    }
  }
}
